/**
 * Semantisch mode: Eingänge whose attachments match by meaning (/search?scope=posteingang).
 * The screen loads those rows via attachment_hits, so the server's gates apply.
 * semanticAvailable=false → fall back to Volltext, like the Ablage. mock → demo topics.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { posteingangSemanticMock } from '../mock';

/** Minimum query length before a search fires (the Ablage / Policies constant). */
export const POSTEINGANG_SEARCH_MIN_LENGTH = 2;
/** Debounce window (ms), the Ablage / Policies precedent. */
export const POSTEINGANG_SEARCH_DEBOUNCE_MS = 250;

export function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export interface SemanticHit {
  id: string;
  /** The attachment that matched, shown on the row as "Treffer im Anhang". */
  filename: string | null;
}

export function usePosteingangSemantic(q: string, enabled: boolean) {
  const live = isLiveData();
  const query = useQuery<{ hits: SemanticHit[]; semanticAvailable: boolean }>({
    queryKey: ['posteingang', 'semantic', q, live ? 'live' : 'mock'],
    queryFn: async () => {
      if (!live) return { hits: posteingangSemanticMock(q), semanticAvailable: true };
      const res = await apiClient.search.query(q, { scope: 'posteingang', limit: 50 });
      return {
        hits: res.items.map((h) => ({ id: h.id, filename: h.subtitle ?? null })),
        semanticAvailable: res.semantic_available !== false,
      };
    },
    enabled,
    staleTime: 30_000,
  });
  return {
    hits: enabled ? (query.data?.hits ?? null) : null,
    isLoading: enabled && query.isLoading,
    semanticAvailable: query.data?.semanticAvailable !== false,
  };
}
